// src/components/groups/FederationMemberDetailModal.jsx
// Federation Members section, on-demand detail (addendum-1, 13 Sep 2026) — see
// docs/briefs/2026-09-13-federation-member-list-addendum-1-names-and-modal.md.
// Read-only: a member group's federation application answers, shown only when the
// Federator deliberately opens this member (the list itself is names-only). RLS on
// group_join_requests, not this modal, is the actual access control (§0.2 of the base
// brief) — this is a presentation choice.
// Pattern followed: ExportDataInfoModal.jsx (Tailwind panelClassName on core/Modal,
// no themed header, StandardButton for the close action).

import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import Modal from '../core/Modal';
import StandardButton from '../core/StandardButton';

const FederationMemberDetailModal = ({ isOpen, onClose, member }) => {
  const { t } = useTranslation('groups');
  const theme = useTheme();

  if (!member) return null;

  // Schema-matched by stable key, current federation schema, raw-key fallback for an
  // answer whose key no longer exists — 4C §2's rule, unchanged by this addendum.
  // Third local instance of this render shape (AdminGroup.jsx's now-removed inline
  // block was the second, the 4C pending-requests list is the first) — flagged to
  // docs/reuse-backlog.md, extraction deferred to Michael/Architect per that entry.
  const schemaByKey = (member.federation_application_schema || []).reduce((acc, field) => {
    acc[field.key] = field;
    return acc;
  }, {});
  const answerEntries = Object.entries(member.request_data || {});

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelClassName="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 p-6 max-h-[80vh] overflow-y-auto"
    >
      <div className="flex items-center gap-2 mb-4">
        {member.logo_url ? (
          <img
            src={member.logo_url}
            alt={member.name}
            className="w-8 h-8 rounded object-cover flex-shrink-0"
          />
        ) : (
          <div
            className="w-8 h-8 rounded flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
            style={{ background: theme?.primary || '#BB4D9A' }}
          >
            {member.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
        <h3 className="text-lg font-semibold text-gray-900 truncate">
          {member.name || t('unknown_group')}
        </h3>
      </div>

      {/* A member with no application on file (the group_links/4E migration case)
          shows that explicitly — not an error, not an empty modal. */}
      {answerEntries.length > 0 ? (
        <div className="space-y-2">
          {answerEntries.map(([key, value]) => (
            <div key={key} className="text-sm text-gray-700">
              <span className="font-semibold">{schemaByKey[key]?.label || key}:</span>{' '}
              {String(value)}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">{t('no_federation_member_application')}</p>
      )}

      <div className="flex justify-end mt-5">
        <StandardButton
          variant="utility"
          label={t('common:close')}
          onClick={onClose}
        />
      </div>
    </Modal>
  );
};

export default FederationMemberDetailModal;
